'use client'

import React, { useEffect, useState } from 'react'
import styles from './styles.module.css'
import SpecsTable from '../Utils/ItemSpecifications/SpecsTable'
import Text from '../../../Text'
import { findItemByType } from '../../../../utils/shopItems'
// Offers migrated off Overmind (Phase D1): the render-time offers come from the
// RegionOffers seam (server-prop grid / full-after-fetch store offers); the
// post-mount full-offers fetch is the offers store's ensureFullOffers.
import { useRegionOffers } from '../../../RegionOffers'
import { useRegionCountryCode } from '../../../RegionGeo'
import { useOffersStore } from '../../../../store/offersStore'

function SpecsPage({ itemType }) {
  const { offers, offersFull } = useRegionOffers()
  const region = useRegionCountryCode()
  const ensureFullOffers = useOffersStore((s) => s.ensureFullOffers)

  // MOUNTED GATE (#127) — the ONLY reliable defence against a hydration mismatch
  // driven by an async refetch that resolves DURING hydration.
  //
  // WHY: `specs` lives ONLY on the FULL offers. The SSR payload is the grid
  // projection (projectOffersForShopGrid), which DROPS `specs` (task #76), so the
  // SSR render has no specs and renders the loading state. The post-mount refetch
  // below then loads the full offers WITH specs and flips `shopItemsFull` true.
  // Under the hermetic fixture harness (and any warm cache) that fetch resolves so
  // fast it can land BEFORE React commits hydration — so the client's first
  // committed render already saw specs and produced the specs <div> where the SSR
  // HTML had the loader. That is the "Expected server HTML to contain a matching
  // <div>" -> React #418/#422 mismatch on this route. Gating on `shopItemsFull`
  // alone does NOT fix it because that flag can already be true by first render.
  //
  // FIX: gate the real content behind `mounted`, which is GUARANTEED false on the
  // SSR pass AND on the client's first (hydration) render — effects never run
  // during render — no matter how fast the refetch resolves. So SSR HTML === the
  // client's first render (both render the loader), eliminating the mismatch; the
  // specs paint one tick after hydration commits (visually identical to before).
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])

  useEffect(() => {
    // ensureFullOffers is idempotent (store-level #188 dedup + #187 full-vs-grid),
    // so the mount call subsumes the former `!shopItemsFull && !shopItemsLoading`
    // gate: `specs` live only on the FULL offers, so this pulls them post-mount.
    ensureFullOffers(region)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Render the loader (a plain, deterministic CSS spinner — NOT MUI's
  // CircularProgress, whose emotion-injected styles/classes differ SSR vs client
  // and were themselves a hydration-mismatch source here) until mounted AND the
  // full offers (with specs) have loaded.
  if (!mounted || !offersFull) {
    return (
      <div className={styles.loading}>
        <span className={styles.spinner} aria-label="Loading" role="status"/>
      </div>
    )
  }

  // Guard against a section whose itemType no longer matches any backend offer
  // (the catalog gets restructured/renamed). Render nothing rather than crash.
  // (e.g. dfp20-wheel-base/specs itemType "WHEEL_BASE".) Reached only after the
  // full offers are loaded, so this is a genuine "no specs for this item" case.
  const item = findItemByType(itemType, offers)
  const specsByCategory = item?.specs?.specsByCategory
  if (!specsByCategory) {
    return null
  }

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.contentWrapper}>
          { Object.keys(specsByCategory).map(category =>
            <div className={styles.specsTableWrapper} key={category}>
              <div className={styles.title}><Text type="heading-3">{category}</Text></div>
              <SpecsTable
                specs={specsByCategory[category]}
                theme={'dark'}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default SpecsPage
